import React, { useState } from "react";

// A product's price: one fixed price, or a price per size (L 1000, M 500, S 100), each with its own original and
// discounted price. Size-based rows are the product's sizes too, and the product's own price is the cheapest size's
// (lists, sorting and the "From ₹…" on the store use it).
// With a fixed price the product can still come in sizes at that one price (S, M, L), added here too.
// p: the product; onChange(changes) gets the fields to update; presets: quick size chips for the store's kind
// ({ "Women": ["S", "M", ...] }); optionPlaceholder: the hint for what the sizes are called.
const MAX_SIZES = 12;
export default function PriceFields({ p, onChange, optionName = "Size", presets = {}, optionPlaceholder = "Size (or Length, Width, Weight)" }) {
  const rows = Array.isArray(p.sizePrices) ? p.sizePrices : [];
  const sizeBased = rows.length > 0;
  const sizes = p.sizes || [];
  const [typed, setTyped] = useState("");
  const hasSize = (s) => sizes.some((x) => x.toLowerCase() === s.toLowerCase());
  const toggleSize = (s) => onChange({ sizes: hasSize(s) ? sizes.filter((x) => x.toLowerCase() !== s.toLowerCase()) : [...sizes, s].slice(0, MAX_SIZES) });
  const addTyped = () => {
    const added = typed.split(",").map((s) => s.trim().slice(0, 32)).filter((s) => s && !hasSize(s));
    if (added.length) onChange({ sizes: [...sizes, ...added].slice(0, MAX_SIZES) });
    setTyped("");
  };
  const presetSizes = Object.values(presets).flat();

  const setRows = (next) => {
    const priced = next.filter((r) => String(r.size || "").trim() && r.price !== "" && Number.isFinite(Number(r.price)));
    const cheapest = priced.reduce((low, r) => (!low || Number(r.price) < Number(low.price) ? r : low), null);
    const sizes = [...new Set(next.map((r) => String(r.size || "").trim()).filter(Boolean))];
    onChange({
      sizePrices: next,
      sizes,
      ...(cheapest ? { price: Number(cheapest.price), originalPrice: Number(cheapest.originalPrice) || 0 } : {}),
    });
  };
  const setRow = (i, key, value) => setRows(rows.map((r, j) => (j === i ? { ...r, [key]: key === "size" ? value.slice(0, 32) : value === "" ? "" : Math.max(0, Number(value)) } : r)));
  const choose = (mode) => {
    if (mode === "size" && !sizeBased) {
      // Starts from the sizes the product already has, at its current prices
      const start = (p.sizes || []).length ? p.sizes : [""];
      setRows(start.map((size) => ({ size, originalPrice: Number(p.originalPrice) || "", price: Number(p.price) || "" })));
    }
    if (mode === "fixed" && sizeBased) onChange({ sizePrices: [] });
  };

  return (
    <div className="price-fields">
      <div className="price-mode" role="radiogroup" aria-label="Price type">
        <label><input type="radio" checked={!sizeBased} onChange={() => choose("fixed")} /> Fixed price</label>
        <label><input type="radio" checked={sizeBased} onChange={() => choose("size")} /> Size based price</label>
      </div>
      {!sizeBased ? (
        <div className="editor-two-cols">
          <label className="admin-field">Original price (₹)<input type="number" min="0" value={p.originalPrice ?? p.price ?? 0} onChange={(e) => onChange({ originalPrice: Number(e.target.value) })} /></label>
          <label className="admin-field">Discounted / selling price (₹) *<input type="number" min="0" value={p.price ?? 0} onChange={(e) => onChange({ price: Number(e.target.value) })} /></label>
          <div className="fixed-sizes">
            <b>{optionName}s <em>{sizes.length ? `${sizes.length} added, all at this price` : "optional: leave empty to sell without a size"}</em></b>
            {Object.entries(presets).map(([label, list]) => (
              <div className="variant-preset" key={label}>
                <small>{label}</small>
                <div className="variant-chips">
                  {list.map((s) => <button type="button" key={s} className={`variant-chip ${hasSize(s) ? "on" : ""}`} aria-pressed={hasSize(s)} onClick={() => toggleSize(s)}>{s}</button>)}
                </div>
              </div>
            ))}
            {sizes.some((s) => !presetSizes.includes(s)) && (
              <div className="variant-chips">
                {sizes.filter((s) => !presetSizes.includes(s)).map((s) => <button type="button" key={s} className="variant-chip on" onClick={() => toggleSize(s)} aria-label={`Remove ${s}`}>{s} ×</button>)}
              </div>
            )}
            <div className="variant-add">
              <input value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTyped())} placeholder="Type with commas, e.g. L, M, S or 10 cm, 20 cm" maxLength={120} />
              <button type="button" className="outline-btn" onClick={addTyped}>Add</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="size-price-table">
          <div className="size-price-row size-price-head"><span>{optionName}</span><span>Original price (₹)</span><span>Discounted price (₹)</span><span /></div>
          {rows.map((r, i) => (
            <div className="size-price-row" key={i}>
              <input value={r.size || ""} onChange={(e) => setRow(i, "size", e.target.value)} placeholder="e.g. L, 10 cm, 100 inch" aria-label={`${optionName} ${i + 1}`} />
              <input type="number" min="0" step="any" value={r.originalPrice ?? ""} onChange={(e) => setRow(i, "originalPrice", e.target.value)} placeholder="Original" aria-label={`Original price ${i + 1}`} />
              <input type="number" min="0" step="any" value={r.price ?? ""} onChange={(e) => setRow(i, "price", e.target.value)} placeholder="Discounted" aria-label={`Discounted price ${i + 1}`} />
              {rows.length > 1 ? <button type="button" className="size-price-remove" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label={`Remove row ${i + 1}`}>×</button> : <span />}
            </div>
          ))}
          <button type="button" className="outline-btn" onClick={() => setRows([...rows, { size: "", originalPrice: "", price: "" }])} disabled={rows.length >= 12}>+ Add more</button>
          <small className="field-help">Shoppers pick a {optionName.toLowerCase()} and pay its discounted price. The original price is shown crossed out when it is higher.</small>
        </div>
      )}
      {(sizeBased || sizes.length > 0) && (
        <label className="admin-field size-name-field">What shoppers pick is called
          <input value={p.optionLabel || ""} maxLength={20} onChange={(e) => onChange({ optionLabel: e.target.value })} placeholder={optionPlaceholder} />
        </label>
      )}
    </div>
  );
}
